// Shared pieces for the Insights page. Everything here mirrors the homepage:
// dark "rate-board" panel for inputs, white "card-light" cards for results,
// flat blue buttons, round flag images, mono green/red numbers.
import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';

// --- dark panel controls (hero) --------------------------------------------
export const inputDark =
  'w-full h-12 px-4 rounded-lg bg-white/5 border border-white/20 text-white text-sm placeholder-white/40 ' +
  'focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/20 transition';

export function LabelDark({ children }) {
  return <label className="block text-[11px] font-semibold uppercase tracking-wider text-blue-200/50 mb-1.5">{children}</label>;
}

export function SelectDark({ value, onChange, children, ...rest }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} className={`${inputDark} appearance-none pr-10 cursor-pointer`}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }} {...rest}>
        {children}
      </select>
      <ChevronDown className="w-4 h-4 text-white/50 absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}
export function OptionDark({ children, ...rest }) {
  return <option className="bg-[#001435] text-white" {...rest}>{children}</option>;
}

// --- light card controls (planner) ----------------------------------------
export const inputLight =
  'w-full h-11 px-3.5 rounded-lg bg-white border border-slate-200 text-slate-900 text-sm ' +
  'focus:border-blue-500 focus:outline-none focus:ring-[3px] focus:ring-blue-500/15 transition';

export function LabelLight({ children }) {
  return <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{children}</label>;
}

export function SelectLight({ value, onChange, children, ...rest }) {
  return (
    <div className="relative">
      <select value={value} onChange={onChange} className={`${inputLight} appearance-none pr-9 cursor-pointer`}
        style={{ WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none', backgroundImage: 'none' }} {...rest}>
        {children}
      </select>
      <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
    </div>
  );
}

// --- buttons (same as .btn-primary on the homepage) -------------------------
export const btnPrimary =
  'h-12 px-6 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-semibold text-sm inline-flex items-center justify-center gap-2 ' +
  'transition-all duration-200 hover:-translate-y-px hover:shadow-[0_4px_12px_rgba(59,130,246,0.4)] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-none';
export const btnOutline =
  'h-11 px-4 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 font-semibold text-sm inline-flex items-center justify-center gap-2 transition';

// --- misc --------------------------------------------------------------------
export function Spinner({ className = 'w-4 h-4' }) {
  return <Loader2 className={`${className} animate-spin`} />;
}

export function SectionLabel({ children }) {
  return <p className="text-xs font-bold uppercase tracking-[0.12em] text-blue-500 mb-2">{children}</p>;
}

export function Flag({ code, className = 'w-7 h-7' }) {
  const known = ['AED','ARS','AUD','BRL','CAD','CHF','CLP','CNY','CRC','CZK','DKK','EGP','EUR','FJD','GBP','HKD','HUF','IDR','ILS','INR','JPY','KRW','MXN','MYR','NOK','NZD','PEN','PHP','PLN','SAR','SCP','SEK','SGD','THB','TRY','TWD','USD','ZAR','IQD','VND'];
  if (!known.includes(code)) {
    return <span className={`${className} rounded-full bg-blue-500/20 text-blue-200 text-[10px] font-bold inline-flex items-center justify-center`}>{code?.slice(0, 3)}</span>;
  }
  return <img src={`/flags/${code}.png`} alt={code} className={`${className} rounded-full object-cover`} />;
}

export const money = (n, digits = 0) =>
  n == null ? '—' : `$${Number(n).toLocaleString('en-CA', { maximumFractionDigits: digits })}`;
export const fmtDate = (iso) =>
  iso ? new Date(iso + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : '';
export const fmtRate = (r) => (r == null ? '—' : r >= 100 ? r.toFixed(0) : r.toFixed(4));
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// --- currency picker with flag images (native <select> can't show images) ---
export function CurrencySelect({ value, onChange, currencies, testId }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const current = currencies.find((c) => c.code === value);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc); document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, []);

  const q = query.trim().toLowerCase();
  const list = q ? currencies.filter((c) => c.code.toLowerCase().includes(q) || (c.name || '').toLowerCase().includes(q)) : currencies;

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => { setOpen((o) => !o); setQuery(''); }} data-testid={testId} aria-haspopup="listbox" aria-expanded={open}
        className={`${inputDark} flex items-center gap-3 text-left`}>
        {current ? <Flag code={current.code} className="w-6 h-6" /> : null}
        <span className="flex-1 truncate">{current ? <><span className="font-semibold">{current.code}</span> <span className="text-white/60">{current.name}</span></> : 'Select currency'}</span>
        <ChevronDown className={`w-4 h-4 text-white/50 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full rounded-xl bg-[#0a1628] border border-white/10 shadow-xl overflow-hidden">
          <div className="p-2 border-b border-white/10">
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search currency…"
              className="w-full h-9 px-3 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:border-blue-500" />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto py-1">
            {list.map((c) => (
              <li key={c.code} role="option" aria-selected={c.code === value}>
                <button type="button" onClick={() => { onChange(c.code); setOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-white/10 ${c.code === value ? 'bg-blue-500/20' : ''}`}>
                  <Flag code={c.code} className="w-6 h-6" />
                  <span className="font-semibold text-white w-12">{c.code}</span>
                  <span className="text-white/60 truncate">{c.name}</span>
                </button>
              </li>
            ))}
            {list.length === 0 && <li className="px-3 py-3 text-sm text-white/40">No match</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
